import { IntakeAnswerTable } from '@/features/clinician/IntakeAnswerTable';
import {
  IntakeHighlights,
  highlightsForForm,
  intakeSubmissionDate,
} from '@/features/clinician/IntakeHighlights';
import { findIntakeOutliers } from '@/features/clinician/intakeOutliers';
import type { IntakeOutlier } from '@/features/clinician/intakeOutliers';
import type { IntakeFormPreview } from '@/types/clinician';

/** The programmatic height / weight / BMI / goal checks the clinician sees. */
export function IntakeOutlierList({
  outliers,
  emptyLabel = 'No out-of-range height, weight, or goal values.',
}: {
  outliers: IntakeOutlier[];
  emptyLabel?: string | null;
}) {
  if (!outliers.length) {
    return emptyLabel ? (
      <p className="type-label-sm text-ink-muted">{emptyLabel}</p>
    ) : null;
  }
  return (
    <ul className="flex flex-col gap-5" aria-label="Programmatic intake checks">
      {outliers.map((outlier) => (
        <li
          key={outlier.code}
          className="border border-danger/30 bg-danger-wash px-9 py-7"
        >
          <p className="type-label-sm font-medium">{outlier.label}</p>
          <p className="type-label-sm text-ink-secondary mt-2">
            {outlier.detail}
          </p>
        </li>
      ))}
    </ul>
  );
}

/** One intake form as the clinician sees it: name, source, date, labeled answers. */
export function IntakeFormAnswers({
  form,
  required = false,
}: {
  form: IntakeFormPreview;
  required?: boolean;
}) {
  return (
    <div>
      <p className="type-label-sm font-medium">
        {required ? 'Required · ' : ''}
        {form.form_name}
      </p>
      <p className="text-11 text-ink-muted mb-6">
        {form.source} · {intakeSubmissionDate(form.submitted_at)}
      </p>
      <IntakeAnswerTable answers={form.answers} highlightSafety />
    </div>
  );
}

/** Checks + highlights + labeled answers for the forms tied to an order
 *  (relevant first; the rest behind a toggle). Ops-facing mirror of the
 *  clinician brief. */
export function IntakePreviewBody({
  forms,
  relevantIds,
  showAll,
  onToggleAll,
}: {
  forms: IntakeFormPreview[];
  relevantIds: string[];
  showAll: boolean;
  onToggleAll: () => void;
}) {
  const relevant = forms.filter((form) =>
    relevantIds.includes(form.form_id ?? ''),
  );
  const primary = relevant.length ? relevant : forms;
  const others = forms.filter((form) => !primary.includes(form));
  const outliers = findIntakeOutliers(primary);
  const highlights = primary.flatMap((form) => highlightsForForm(form));

  if (!forms.length) {
    return (
      <p className="type-label-sm text-ink-muted">
        No intake submissions on record for this patient.
      </p>
    );
  }
  return (
    <>
      <div>
        <p className="font-mono text-10 uppercase text-ink-muted mb-5">
          Programmatic intake checks
        </p>
        <IntakeOutlierList outliers={outliers} />
      </div>
      {highlights.length > 0 && (
        <div>
          <p className="font-mono text-10 uppercase text-ink-muted mb-5">
            Clinical highlights
          </p>
          <IntakeHighlights entries={highlights} columns={1} />
        </div>
      )}
      {primary.map((form) => (
        <IntakeFormAnswers
          key={form.id}
          form={form}
          required={relevant.includes(form)}
        />
      ))}
      {others.length > 0 && (
        <div>
          <button
            type="button"
            onClick={onToggleAll}
            className="type-label-sm text-accent hover:underline"
          >
            {showAll
              ? 'Hide other forms'
              : `Show all forms (${others.length} more)`}
          </button>
          {showAll &&
            others.map((form) => (
              <div key={form.id} className="mt-8">
                <IntakeFormAnswers form={form} />
              </div>
            ))}
        </div>
      )}
    </>
  );
}
